import dropdownEnums, {
  Enums,
  getObjectKeyByValue,
} from "@/services/dropdown-enums";
import {
  Validate_Email,
  Validate_Number,
} from "./graphql-queries/general-queries";
import { Box, Chip, Typography } from "@mui/material";
import { GetFontAwesomeIcon } from "@/components/font-awesome-icon";
import { faStar } from "@fortawesome/free-solid-svg-icons";
import DefaultChip from "../settings/components/details/content/lead-status/default-chip";
import UIPopper from "@/components/popover";
import { makeRenderValue } from "../settings/components/details/content/task-settings/task-reminders/view-task-reminder-list";
import moment from "moment";
import CopyButton from "@/components/copy-button";
import { faLink } from "@fortawesome/free-solid-svg-icons/faLink";
import {
  camelCaseStringToNormalText,
  getFormattedDate,
  getTimezoneAbbreviation,
} from "@/services/generic.methods";
import { colorEnums } from "@/modules/crm/dashboard/helper";

export const ColumnTypesConstant = ["Text", "Number", "Date"];

export const CompareType = {
  hasAnyValue: "has_any_value",
  between: "between_range",
};
export const CompareOperator = {
  Begins: "startswith",
  Ends: "endswith",
  Includes: "contains",
  Does_not_includes: "notcontains", // discussion
  Equal: "equal",
  Does_not_equal: "notequal",
  // Is_provided: "_equal", // discussion
  // Is_not_provided: "Does_not_equal", // discussion
  Between: "betweenrange",
  Before: "lessthan", // discussion
  After: "greaterthan", // discussion
  Greater_than: "greaterthan",
  Less_than: "lessthan",
};

export const DefaultLeadCols = [
  // "id",
  "salutation",
  "firstName",
  "lastName",
  "leadAppointments",
  "countryCode",
  "phone",
  "email",
  "assignedTo",
  "leadStatusId",
  "optIn",
  // "value",
];

export const LEAD_APPOINTMENTS_COLUMN_META = {
  columnDisplayName: "Appointment Types",
  columnName: "LeadAppointments",
  columnType: "List`1",
};

export const LEAD_IDENTITY_COLUMN_NAMES = [
  // "Id",
  // "Salutation",
  // "FirstName",
  // "LastName",
  // "LeadAppointments",
];

/** Keep identity columns (and Appointment Types) in a stable prefix order. */
export const orderLeadGridIdentityColumns = (cols = []) => {
  const byName = new Map(cols.map((col) => [col.columnName, col]));
  const prefix = [];
  const used = new Set();

  LEAD_IDENTITY_COLUMN_NAMES.forEach((name) => {
    if (name === "LeadAppointments") {
      prefix.push(LEAD_APPOINTMENTS_COLUMN_META);
      used.add(name);
      return;
    }
    if (byName.has(name)) {
      prefix.push(byName.get(name));
      used.add(name);
    }
  });

  const rest = cols.filter((col) => !used.has(col.columnName));
  return [...prefix, ...rest];
};

export const orderLeadGridIdentityColumnNames = (columns = []) => {
  const used = new Set();
  const prefix = [];

  LEAD_IDENTITY_COLUMN_NAMES.forEach((name) => {
    const found = columns.find(
      (col) => String(col).toLowerCase() === name.toLowerCase()
    );
    if (found) {
      prefix.push(found);
      used.add(String(found).toLowerCase());
    }
  });

  return [
    ...prefix,
    ...columns.filter((col) => !used.has(String(col).toLowerCase())),
  ];
};

export const BASIC_LEAD_COLUMNS = [
  "Id",
  "Salutation",
  "FirstName",
  "LastName",
  "Email",
  "Name",
  "Phone",
  "CompanyKey",
  "LeadSourceId",
  "LeadTypeId",
  "CreatedOn",
  "ModifiedOn",
];

export const BASIC_LEAD_COLUMNS_META = [
  {
    columnName: "Salutation",
    columnType: "Text",
    columnDisplayName: "Salutation",
  },
  {
    columnName: "FirstName",
    columnType: "Text",
    columnDisplayName: "First Name",
  },
  {
    columnName: "LastName",
    columnType: "Text",
    columnDisplayName: "Last Name",
  },
  { columnName: "Email", columnType: "Text", columnDisplayName: "Email" },
  { columnName: "Phone", columnType: "Text", columnDisplayName: "Phone" },
  {
    columnName: "LeadSourceId",
    columnType: "Text",
    columnDisplayName: "Lead Source",
  },
  {
    columnName: "LeadTypeId",
    columnType: "Text",
    columnDisplayName: "Lead Type",
  },
  {
    columnName: "CreatedOn",
    columnType: "Date",
    columnDisplayName: "Created On",
  },
];

export const BASIC_DEFAULT_LEAD_COLS = BASIC_LEAD_COLUMNS_META.map(
  (col) => col.columnName.charAt(0).toLowerCase() + col.columnName.slice(1)
);

export const BASIC_FORM_ALLOWED_SECTIONS = ["LeadInformation", "Description"];
export const BASIC_FORM_ALLOWED_FIELDS = [
  "salutation",
  "firstName",
  "lastName",
  "email",
  "phone",
  "leadSourceId",
  "leadTypeId",
  "description",
];

/** Basic plan: fixed Lead Source dropdown (Blazeo stores source as string). */
export const applyBasicLeadSourceFieldConfig = (field) => {
  if (field?.name !== "leadSourceId") return field;
  return {
    ...field,
    title: "Lead Source",
    renderChip: false,
    clearable: false,
  };
};

/** Basic plan: hide "None" from Lead Type dropdown (appointment-only create lead). */
export const applyBasicLeadTypeFieldConfig = (field) => {
  if (field?.name !== "leadTypeId") return field;
  return {
    ...field,
    clearable: false, // hides UISelect "None" menu item
    // clearable: true,
    // noneSelectable: true,
  };
};

const leadStatusId = {
  title: "Lead Status",
  value: "",
  name: "leadStatusId",
  type: "select",
  source: {
    store: "leadStatuses",
    label: "name",
    value: "id",
  },
  options: [],
  clearable: false,
  customCode: { label: "[Lead Status]", value: "Open" },
};

const assignedTo = {
  title: "Assigned to",
  value: "",
  name: "assignedTo",
  type: "auto-complete",
  source: {
    store: "users",
    label: "displayName",
    alternateLabel: "username",
    value: "id",
  },
  options: [{ label: "Unassigned", value: 0 }],
  customCode: { label: "[Lead Assignee]", value: "Paul" },
};

const leadValue = {
  title: "Lead Value",
  value: "",
  name: "leadValue",
  type: "number",
};

export const massUpdateFields = [assignedTo, leadStatusId];

export const leadPhone = {
  title: "Phone",
  value: "",
  name: "phone",
  type: "phone",
  selectName: "countryCode",
  rules: {
    required: false,
    validation: "phone",
    invalidError: "The phone number is invalid",
    // pattern: "^(\\+?1[-.\\s]?)?\\(?\\d{3}\\)?[-.\\s]?\\d{3}[-.\\s]?\\d{4}$",
    gqlValidate: {
      error: "The phone number already exists",
      query: Validate_Number,
    },
  },
};

const leadPhoneCountryCode = {
  title: "Country Code",
  value: "",
  name: "countryCode",
  type: "text",
  fieldHidden: true,
};

const {
  After,
  Before,
  Begins,
  Ends,
  Between,
  Equal,
  Does_not_equal,
  Includes,
  Does_not_includes,
  Greater_than,
  Less_than,
} = CompareOperator;

export const createOptions = (
  data = [],
  labelKey = "value",
  valueKey = "key"
) => {
  let options = data.map((item) => {
    const label = item[labelKey];
    const value = item[valueKey];

    return { label, value };
  });
  return options;
};

export const basicInfoColumns = (callbacks = {}) => [
  {
    title: "Salutation",
    value: "",
    name: "salutation",
    type: "select",
    fieldHidden: true,
    customCode: { label: "[Lead Salution]", value: "Mr" },
    comparisionType: createOptions([
      Equal,
      Does_not_equal,
      // is_provided,
      // is_not_provided,
    ]),
  },
  {
    title: "First Name",
    value: "",
    name: "firstName",
    type: "inputSelectCombo",
    selectName: "salutation",
    rules: {
      required: false,
      limit: 50,
    },
    callbacks,
    customCode: { label: "[Lead FirstName]", value: "Jack" },
    comparisionType: createOptions([
      Begins,
      Ends,
      Includes,
      Does_not_includes,
      Equal,
      Does_not_equal,
      // is_provided,
      // is_not_provided,
    ]),
  },
  {
    title: "Last Name",
    value: "",
    name: "lastName",
    type: "text",
    rules: {
      required: false,
      limit: 50,
    },
    customCode: { label: "[Lead LastName]", value: "Smith" },
    comparisionType: createOptions([
      Begins,
      Ends,
      Includes,
      Does_not_includes,
      Equal,
      Does_not_equal,
      // is_provided,
      // is_not_provided,
    ]),
  },
  {
    title: "Email",
    value: "",
    name: "email",
    type: "text",
    rules: {
      required: true,
      validation: "email",
      gqlValidate: {
        error: "The email already exists",
        query: Validate_Email,
      },
    },
    customCode: { label: "[Lead Email]", value: "jack@gmail.com" },
    comparisionType: createOptions([
      Begins,
      Ends,
      Includes,
      Does_not_includes,
      Equal,
      Does_not_equal,
      // is_provided,
      // is_not_provided,
    ]),
  },
  {
    title: "Alternate Email",
    value: "",
    name: "alternateEmail",
    type: "text",
    fieldHiddenTemp: true,
    rules: {
      required: false,
      validation: "email",
      // invalidError: "The alternate email is invalid",
      // alternateTo: "email", // primary field name, which should not be duplicated
      // alternateError: "The alternate email must not be same as primary",
    },
  },
  // { ...leadPhoneCountryCode },
  {
    ...leadPhone,
    customCode: { label: "[Lead Phone]", value: "(925) 293-8670" },
    comparisionType: createOptions([
      Begins,
      Ends,
      Includes,
      Does_not_includes,
      Equal,
      Does_not_equal,
      // is_provided,
      // is_not_provided,
    ]),
  },
  {
    title: "Alternate Phone",
    value: "",
    name: "alternatePhone",
    type: "phone",
    selectName: "alternateCountryCode",
    fieldHiddenTemp: true,
    rules: {
      required: false,
      validation: "phone",
      alternateTo: "phone", // primary field name, which should not be duplicated
      invalidError: "The alternate phone number is invalid",
      alternateError: "The alternate phone must not be same as primary",
    },
  },
  {
    title: "Created",
    value: "",
    name: "createdOn",
    type: "date",
    disabled: true,
    fieldHidden: true,
    comparisionType: createOptions([Equal, Before, Between, After]),
  },
  {
    title: "Modified",
    value: "",
    name: "modifiedOn",
    type: "date",
    disabled: true,
    fieldHidden: true,
    comparisionType: createOptions([Equal, Before, Between, After]),
  },
  {
    title: "Last Activity",
    value: "",
    name: "lastActivity",
    type: "date",
    disabled: true,
    fieldHidden: true,
    comparisionType: createOptions([Equal, Before, Between, After]),
  },
  {
    title: "Preferred contact method",
    value: "",
    name: "preferredContactId",
    type: "select",
    source: {
      store: "preferredContact",
      label: "name",
      value: "id",
    },
    options: [],
    rules: { required: false },
    comparisionType: createOptions([
      Equal,
      Does_not_equal,
      // is_provided,
      // is_not_provided,
    ]),
  },
  {
    title: "Designation",
    value: "",
    name: "designation",
    type: "text",
    fieldHidden: true,
  },
  // {
  //   title: "Lead Value",
  //   value: "",
  //   name: "value",
  //   type: "number",
  //   tooltip: "The monetary value of the lead",
  //   rules: {
  //     required: false,
  //     validation: "custom",
  //     pattern: "^\\d{1,7}$",
  //   },
  // },
  {
    title: "Lead Owner",
    value: "",
    name: "leadOwnerId",
    type: "select",
    source: {
      store: "users",
      label: "displayName",
      value: "id",
    },
    clearable: false,
    options: [],
    customCode: { label: "[Lead Owner]", value: "David" },
    comparisionType: createOptions([Equal, Does_not_equal]),
  },
  {
    ...assignedTo,
    fieldHidden: false,
    comparisionType: createOptions([
      Equal,
      Does_not_equal,
      // is_provided,
      // is_not_provided,
    ]),
  },
  {
    ...leadStatusId,
    fieldHidden: false,
    comparisionType: createOptions([Equal, Does_not_equal]),
  },
  {
    title: "Lead Type",
    value: "",
    name: "leadTypeId",
    type: "select",
    options: createOptions(dropdownEnums.enum_LeadType, "name", "value"),
    clearable: true,
    noneSelectable: true,
    customCode: { label: "[Lead Type]", value: "Open" },
    comparisionType: createOptions([Equal, Does_not_equal]),
  },
  {
    title: "Language",
    value: "",
    name: "languageId",
    type: "select",
    source: {
      store: "languages",
      label: "name",
      value: "id",
    },
    options: [],
    customCode: { label: "[Lead Language]", value: "English" },
    comparisionType: createOptions([
      Equal,
      Does_not_equal,
      // is_provided,
      // is_not_provided,
    ]),
  },
  {
    title: "Lead Source",
    value: "",
    name: "leadSourceId",
    type: "select",
    renderChip: true,
    source: {
      store: "leadSources",
      label: "name",
      value: "id",
    },
    options: [],
    customCode: { label: "[Lead Source]", value: "Advertisment" },
    comparisionType: createOptions([
      Equal,
      Does_not_equal,
      // is_provided,
      // is_not_provided,
    ]),
  },
  {
    title: "Annual Revenue",
    value: "",
    name: "annualRevenue",
    type: "number",
    tooltip: "Amount of money the lead makes annually",
    rules: {
      required: false,
      validation: "custom",
      pattern: "^\\d{1,7}(\\.\\d{1,2})?$",
      invalidError: "Enter a valid amount with up to 2 decimal places",
    },
    customCode: { label: "[Lead Revenue]", value: "$100" },
    comparisionType: createOptions([
      Equal,
      Does_not_equal,
      Between,
      Greater_than,
      Less_than,
    ]),
  },
  {
    title: "Tags",
    value: [],
    name: "tags",
    multiple: true,
    type: "tagsInput",
    multiSelect: true,
    source: {
      store: "tags",
      label: "name",
      value: "id",
    },
    colorSource: {
      store: "tagColors",
      label: "name",
      value: "id",
    },
    rules: {
      required: false,
    },
    options: [],
  },
  {
    title: "Opt-in",
    value: false,
    name: "optIn",
    type: "switch",
    tooltip:
      "By enabling this flag, you are consenting to communication on behalf of the lead.",
    rules: { required: false },
  },
];

export const socialColumns = [
  {
    title: "Website",
    value: "",
    name: "website",
    type: "text",
    rules: { required: false, validation: "url" },
  },
];

export const addressColumns = () => [
  {
    title: "Country",
    value: "",
    name: "countryId",
    type: "select",
    source: {
      store: "countries",
      label: "countryName",
      value: "countryId",
    },
    options: [],
    maxLength: "50",
    customCode: { label: "[Lead Country]", value: 1 },
    comparisionType: createOptions([
      Equal,
      Does_not_equal,
      // is_provided,
      // is_not_provided,
    ]),
  },
  {
    title: "State/Province",
    value: "",
    name: "stateProvinceId",
    maxLength: "50",
    type: "select",
    checkType: (values) => {
      if (values?.countryId == 3) {
        return { type: "text", name: "state" };
      } else {
        return {
          type: "select",
        };
      }
    },
    source: {
      store: "statesProvinces",
      parentStore: "countries",
      label: "stateProvinceName",
      value: "stateProvinceId",
      filterCallback: (options, value, countries = []) => {
        if (!value) return [];
        const country = countries.find(
          (c) =>
            String(c.countryGUID) === String(value) ||
            String(c.id) === String(value)
        );
        const dbCountryId = country?.countryGUID || country?.countryId || value;
        return options.filter(
          (op) => String(op.countryId) === String(dbCountryId)
        );
      },
    },

    options: [],
    customCode: { label: "[Lead State]", value: "" },
    comparisionType: createOptions([
      Equal,
      Does_not_equal,
      // is_provided,
      // is_not_provided,
    ]),
  },
  {
    title: "City",
    value: "",
    name: "city",
    type: "text",
    maxLength: "50",
    customCode: { label: "[Lead City]", value: "New York" },
    comparisionType: createOptions([
      Equal,
      Does_not_equal,
      // is_provided,
      // is_not_provided,
    ]),
  },
  {
    title: "Street",
    value: "",
    name: "street",
    type: "text",
    maxLength: "100",
    customCode: { label: "[Lead Street]", value: "15205 ABC Street." },
    comparisionType: createOptions([
      Equal,
      Does_not_equal,
      // is_provided,
      // is_not_provided,
    ]),
  },
  {
    title: "Zip Code",
    value: "",
    name: "zipCode",
    type: "text",
    maxLength: "16",
    customCode: { label: "[Lead ZipCode]", value: "11021" },
    comparisionType: createOptions([
      Begins,
      Ends,
      Includes,
      Does_not_includes,
      Equal,
      Does_not_equal,
      // is_provided,
      // is_not_provided,
    ]),
  },
];

export const descriptionColumns = [
  {
    title: "Description",
    value: "",
    name: "description",
    type: "text-area",
    customCode: { label: "[Lead Description]", value: "This is a dummy text" },
  },
];
const abc = () => {};

export const KnownColumnsList = (callbacks = {}) => [
  ...basicInfoColumns(callbacks),
  ...socialColumns,
  ...addressColumns(callbacks),
  ...descriptionColumns,
  {
    title: "ID",
    value: "",
    name: "id",
    type: "number",
    comparisionType: createOptions([
      Equal,
      Does_not_equal,
      Between,
      Greater_than,
      Less_than,
    ]),
  },
  {
    title: "OptIn",
    value: "",
    name: "optIn",
    type: "boolean",
    comparisionType: createOptions([Equal, Does_not_equal]),
  },
  // { title: "Contact Id", value: "", name: "contactId", type: "number" },
  // { title: "Lead Type", value: "", name: "leadTypeId", type: "number" },
  // { title: "Industry Id", value: "", name: "industryId", type: "number" },
  // { title: "Address Id", value: "", name: "addressId", type: "number" },
  // { title: "Created On", value: "", name: "createdOn", type: "dateTime" },
  // { title: "Modified On", value: "", name: "modifiedOn", type: "dateTime" },
  // { title: "Contact", value: "", name: "contact", type: "text" },
];

export const knownFormFields = {
  id: "form_1",
  sections: [
    {
      id: "LeadInformation",
      heading: "Lead Information",
      infoText: "Email is required.",
      fields: basicInfoColumns(),
    },
    {
      id: "Address",
      heading: "Address",
      fields: addressColumns(),
    },
    {
      id: "CustomFields",
      heading: "Additional Fields",
      fields: [],
      hidden: true, // hidden initially
      showAdd: true, // add custom field initially
    },
    {
      id: "Description",
      heading: "Additional Info",
      fields: descriptionColumns,
    },
  ],
};

export const leadFormMap = {
  address: [
    "street",
    "city",
    "state",
    "stateProvinceId",
    "zipCode",
    "country",
    "countryId",
    ["addressId", "id"],
  ],
  contact: [
    "salutation",
    "firstName",
    "lastName",
    "designation",
    "countryCode",
    "countryISOCode",
    "phone",
    "alternatePhone",
    "alternateCountryCode",
    "email",
    "alternateEmail",
    ["contactId", "id"],
  ],
  leadTagChanges: ["tags"],
};
export const DefaultImportLeadCols = [
  "name",
  "path",
  "totalRecords",
  "recordsCreatedCount",
  "recordsUpdatedCount",
  "source",
  "userId",
  "createdOn",
  "status",
];

export const RenderStatus = ({ value }, options) => {
  let status = options.filter((item) => item.value == value)[0];
  // var(--palette-active)
  let color = `var(--palette-${status?.statusColor})`;
  let bgColor = `var(--palette-${status?.statusColor}_bg)`;
  let text = status?.name;
  // return <span style={{ color: color }}>{text}</span>;
  return (
    <Chip
      size="small"
      sx={{
        color: color,
        bgcolor: bgColor,
      }}
      label={text}
    />
  );
};

const renderBasicLeadSource = ({ data, options }) => {
  const sourceVal =
    data?.source || data?.Source || data?.LeadSource || data?.leadSourceId;
  const isNone = sourceVal && String(sourceVal).toLowerCase() === "none";
  const opts = typeof options === "function" ? options() : [];
  const match = opts.find(
    (o) =>
      String(o.value) === String(sourceVal) ||
      String(o.id) === String(sourceVal)
  );
  return (
    <Typography variant="body2">
      {isNone || !sourceVal ? "N/A" : match?.label || match?.name || sourceVal}
    </Typography>
  );
};

// For setting custom columns such as custom rendering, callbacks etc
export const CustomLeadsColumns = (
  callbacks = {},
  { isBasic = false } = {}
) => [
  {
    columnName: "Id",
    // draggable: false,
    columnType: "number",
    filterType: "number",
    order: 1,
  },
  {
    title: "First Name",
    value: "",
    columnName: "FirstName",
    columnType: "text",
    rules: { required: true },
    callbacks,
    renderCallbackButton: true,
    // draggable: false,
    minWidth: 180,
    order: 3,
    indicator: (
      <Chip
        size="small"
        sx={{
          borderRadius: `2px`,
          fontSize: "0.75rem",
          height: "16px",
          color: "var(--palette-header_text)",
          background: "var(--palette-orange_shade)",
          "& .MuiChip-label": {
            padding: "0px 3px",
          },
        }}
        label={"New"}
      />
    ),
  },
  {
    columnName: "Salutation",
    // draggable: false,
    order: 2,
    columnType: "select",
    minWidth: 130,
  },
  { columnName: "LastName", order: 3 },
  {
    columnName: "LeadAppointments",
    title: "Appointment Types",
    columnType: "tags",
    multiple: true,
    idKey: "calendarId",
    order: 4,
    sortable: false,
    renderRaw: true,
    minWidth: 180,
  },
  { columnName: "Phone", order: 4, columnType: "phone" },
  { columnName: "Email", order: 5 },
  {
    columnName: "PreferredContactId",
    title: "Preferred Contact Method",
    order: 6,
    columnType: "select",
    minWidth: 180,
  },
  { columnName: "LeadOwnerId", columnType: "select", order: 7 },
  { columnName: "AssignedTo", columnType: "select", order: 7 },
  { columnName: "LeadStatusId", columnType: "select", order: 8 },
  { columnName: "OptIn", order: 9, minWidth: 100, columnType: "boolean" },
  { columnName: "OptedOut", order: 9, minWidth: 100, columnType: "boolean" },
  // {
  //   columnName: "Value",
  //   order: 10,
  //   minWidth: 100,
  //   header: "Lead Value",
  //   columnType: "number",
  // },
  { columnName: "AnnualRevenue", order: 11, columnType: "number" },
  {
    columnName: "LeadSourceId",
    order: 12,
    ...(isBasic
      ? { columnType: "text", render: renderBasicLeadSource }
      : { columnType: "tags", renderRaw: true, multiple: false }),
  },
  {
    columnName: "CreatedOn",
    order: 13,
    columnType: "date",
    dateFormat: "DD MMM, YYYY",
  },
  {
    columnName: "ModifiedOn",
    order: 14,
    columnType: "date",
    dateFormat: "DD MMM, YYYY",
  },
  {
    columnName: "LastActivity",
    columnType: "date",
    dateFormat: "DD MMM, YYYY",
  },
  { columnName: "LanguageId", columnType: "select", order: 15 },
  // { columnName: "languageId", order: 15 },
  { columnName: "Street", order: 16 },
  { columnName: "City", order: 17 },
  // { columnName: "State", order: 18 },
  {
    columnName: "StateProvinceId",
    order: 18,
    columnType: "select",
    header: "State/Province",
    render: ({ data, options }) => {
      let opt = options ? options() : [];
      if (data?.stateProvinceId) {
        let val = opt.find(
          (o) =>
            String(o.id) === String(data.stateProvinceId) ||
            String(o.value) === String(data.stateProvinceId)
        );
        return (
          <Typography variant="body2">
            {val?.label || val?.stateProvinceName || "N/A"}
          </Typography>
        );
      } else {
        return <Typography variant="body2">{data?.state || "N/A"}</Typography>;
      }
    },
  },
  { columnName: "ZipCode", order: 19 },
  {
    columnName: "LeadOriginSubTypeName",
    header: "Initiated By",
    columnDisplayName: "Initiated By",
    minWidth: 140,
    render: ({ value }) => {
      if (!value) {
        return <Typography variant="body2">N/A</Typography>;
      }
      let hash = 0;
      const label = String(value);
      const palette = Object.values(colorEnums);
      for (let i = 0; i < label.length; i++) {
        hash = label.charCodeAt(i) + ((hash << 5) - hash);
      }
      const color = palette[Math.abs(hash) % palette.length];
      return (
        <Chip
          size="small"
          label={label}
          sx={{
            bgcolor: color.bg.trim(),
            color: color.text.trim(),
            fontWeight: 500,
          }}
        />
      );
    },
  },
  { columnName: "CountryId", order: 20, columnType: "select" },
  {
    columnName: "LeadTags",
    columnType: "tags",
    multiple: true,
    order: 21,
    sortable: false,
    renderRaw: true,
    minWidth: 180,
  },
  { columnName: "Description", order: 21 },
  { columnName: "Website", hidden: true },
  { columnName: "ContactId", hidden: true },
  { columnName: "LeadTypeId", hidden: false, columnType: "select" },
  { columnName: "IndustryId", hidden: true },
  { columnName: "AddressId", hidden: true },
  { columnName: "Designation", hidden: true },
  { columnName: "LeadAttachments", hidden: true },
  { columnName: "LeadNotes", hidden: true },
];

// Central list of internal/non-displayable grid column names (lowercased)
export const INTERNAL_COLUMN_BLACKLIST = [
  "leadorigintypeid",
  "leadoriginsubtypeid",
  "leadoriginsubtype",
  "leadoriginsubtypename",
  "customfieldvalues",
];

/**
 * Returns true if the given column name refers to an internal/non-displayable column.
 * Comparison is case-insensitive and tolerant of undefined/null input.
 */
export const isInternalColumn = (columnName) => {
  if (!columnName) return false;
  return INTERNAL_COLUMN_BLACKLIST.includes(String(columnName).toLowerCase());
};

export const CustomImportLeadColumns = [
  {
    header: "Name",
    value: "",
    columnName: "name",
    columnType: "text",
    minWidth: 200,
  },
  {
    header: "File name",
    value: "",
    columnName: "path",
    columnType: "text",
    minWidth: 120,
  },
  {
    header: "Total Records",
    value: "",
    columnName: "recordsCount",
    columnType: "number",
    minWidth: 80,
  },
  {
    header: "Records Created",
    value: "",
    columnName: "recordsCreatedCount",
    columnType: "number",
    minWidth: 80,
  },
  {
    header: "Updated Records",
    value: "",
    columnName: "recordsUpdatedCount",
    columnType: "number",
    minWidth: 80,
  },
  {
    header: "Created On",
    value: "",
    columnType: "date",
    columnName: "createdOn",
    dateFormat: "DD MMM, YYYY",
    minWidth: 130,
  },
  {
    header: "Modified On",
    value: "",
    columnName: "modifiedOn",
    columnType: "DateTime?",
    dateFormat: "DD MMM, YYYY",
    minWidth: 130,
  },
  {
    header: "User",
    value: "",
    columnName: "userId",
    columnType: "select",
    minWidth: 100,
  },
  {
    header: "Source",
    value: "",
    columnName: "source",
    columnType: "select",
    minWidth: 100,
    // render: (e) => RenderStatus(e, dropdownEnums.enum_importLeadSource),
  },
  {
    header: "Status",
    value: "",
    columnName: "status",
    columnType: "select",
    render: (e) => RenderStatus(e, dropdownEnums.enum_importLeadStatus),
  },
];

const leadDetailsLeftField = [
  "assignedTo",
  "salutation",
  "firstName",
  "lastName",
  "countryCode",
  "phone",
  "email",
  // "companyName",
];

const filterBasicInfoFrom = () => {
  const basicInfo = leadDetailsLeftField.map((name) => {
    return basicInfoColumns()?.filter((item) => {
      item.updateOnChange = true;
      return item?.name === name;
    })[0];
  });
  return basicInfo.filter(Boolean);
};

export const knownLeadDetailsFormFields = {
  id: "lead_details_page_edit",
  sections: [
    {
      fields: filterBasicInfoFrom(),
    },
  ],
};
export const leadStatusFormField = {
  id: "lead_status_field_form",
  sections: [
    {
      fields: [
        { ...assignedTo, updateOnChange: true },
        { ...leadStatusId, updateOnChange: true },
      ],
    },
  ],
};
export const leadPhoneNumberField = {
  id: "lead_phone_field_form",
  sections: [
    {
      fields: [
        {
          ...leadPhone,
          infoText:
            "Enter a phone number of this lead to continue SMS conversation.",
          updateOnChange: true,
        },
        { ...leadPhoneCountryCode },
      ],
    },
  ],
};

// Lead list columns
export const DefaultLeadListColumns = [
  "firstName",
  "lastName",
  "countryCode",
  "phone",
  "email",
  "leadStatusId",
  "modifiedOn",
];
// Lead list columns
export const LeadListColumns = [
  { columnName: "FirstName", order: 1, enableFilter: false },
  { columnName: "LastName", order: 2, enableFilter: false },
  { columnName: "Phone", order: 3, columnType: "phone", enableFilter: false },
  { columnName: "Email", order: 4, enableFilter: false },
  {
    columnName: "LeadStatusId",
    columnType: "select",
    order: 5,
    enableFilter: false,
  },
  {
    columnName: "ModifiedOn",
    order: 6,
    columnType: "date",
    enableFilter: false,
  },
];

export const ListColumns = (callbacks = {}) => [
  // {
  //   columnName: "Id",
  //   draggable: false,
  //   columnType: "number",
  //   order: 1,
  // },
  {
    header: "Name",
    value: "",
    columnName: "name",
    columnType: "text",
    callbacks,
    draggable: false,
    minWidth: 180,
    order: 3,
  },
  // {
  //   title: "Type",
  //   value: "",
  //   columnName: "type",
  //   columnType: "select",
  //   // callbacks,
  //   draggable: false,
  //   minWidth: 180,
  //   order: 3,
  //   enableFilter: false,
  // },
  {
    header: "Created By",
    value: "",
    columnName: "userDisplayName",
    columnType: "text",
    minWidth: 180,
  },
  {
    header: "Created on",
    value: "",
    columnName: "createdOn",
    columnType: "date",
    minWidth: 180,
    dateFormat: "DD MMM, YYYY",
  },
  {
    header: "Number of Leads",
    value: "",
    columnName: "itemCount",
    columnType: "text",
    callbacks,
    draggable: false,
    minWidth: 180,
    sortable: false,
    enableFilter: false,
  },
  {
    header: "Automations Used In",
    value: "",
    columnName: "automationsUsedIn",
    columnType: "text",
    callbacks,
    sortable: false,
    draggable: false,
    minWidth: 180,
    enableFilter: false,
  },
  {
    header: "Number of Times Used",
    value: "",
    columnName: "numberOfTimeUsed",
    columnType: "text",
    callbacks,
    enableFilter: false,
    draggable: false,
    sortable: false,
    minWidth: 200,
    render: ({ data }) => (
      <Typography variant="body2">
        {data.automationsUsedIn.split(",").filter((a) => a).length}
      </Typography>
    ),
  },
];
export const ContactListColumns = (callbacks = {}) => [
  {
    header: "ID",
    columnName: "leadId",
    draggable: false,
    columnType: "number",
    order: 1,
    render: ({ data }) => {
      return data.leadId || data?.id || "N/A";
    },
  },
  {
    header: "First Name",
    value: "",
    columnName: "firstName",
    columnType: "text",
    callbacks,
    draggable: false,
    minWidth: 180,
    order: 3,
  },
  {
    header: "Last Name",
    value: "",
    columnName: "lastName",
    columnType: "text",
    callbacks,
    draggable: false,
    minWidth: 180,
    order: 3,
  },
  {
    header: "Email",
    value: "",
    columnName: "email",
    order: 13,
    columnType: "text",
    minWidth: 180,
  },
  {
    header: "Phone",
    value: "",
    columnName: "phone",
    order: 13,
    columnType: "text",
    minWidth: 180,
  },
];
export const ManualEnrolledLeadsColumns = (callbacks = {}) => [
  {
    header: "ID",
    columnName: "leadId",
    draggable: false,
    columnType: "number",
  },
  {
    header: "First Name",
    value: "",
    columnName: "firstName",
    columnType: "text",
    callbacks,
    draggable: false,
    minWidth: 180,
  },
  {
    header: "Last Name",
    value: "",
    columnName: "lastName",
    columnType: "text",
    draggable: false,
    minWidth: 180,
  },
  {
    header: "Email",
    value: "",
    columnName: "email",
    columnType: "text",
    minWidth: 180,
  },
  {
    header: "Phone",
    value: "",
    columnName: "phone",
    columnType: "text",
    minWidth: 180,
  },
];

export const WorkflowTemplatColumns = (callbacks = {}) => [
  {
    header: "Name",
    value: "",
    columnName: "name",
    columnType: "text",
    callbacks,
    draggable: false,
    minWidth: 180,
    order: 3,
    className: "cursor-pointer",
  },
  {
    header: "Created Date",
    value: "",
    columnName: "createdOn",
    order: 13,
    columnType: "date",
    minWidth: 180,
    draggable: false,
    dateFormat: "DD MMM, YYYY",
    className: "cursor-pointer",
  },
  {
    header: "Created By",
    value: "",
    columnName: "userDisplayName",
    order: 13,
    columnType: "text",
    minWidth: 180,
    className: "cursor-pointer",
    draggable: false,
  },
  {
    header: "Executed Automations",
    value: "",
    columnName: "executedWorkflows",
    order: 13,
    columnType: "text",
    minWidth: 180,
    draggable: false,
    enableFilter: false,
    sortable: false,
    className: "cursor-pointer",
  },
];
export const WorkflowDefaultTemplatColumns = (callbacks = {}) => [
  {
    header: "Name",
    value: "",
    columnName: "name",
    columnType: "text",
    callbacks,
    draggable: false,
    minWidth: 180,
    order: 3,
    className: "cursor-pointer",
  },
  {
    header: "Status",
    value: "",
    columnName: "templateState",
    order: 13,
    columnType: "select",
    minWidth: 120,
    draggable: false,
    className: "cursor-pointer",
  },
  {
    header: "Business Categories",
    value: "",
    columnName: "businessCategoryIds",
    order: 13,
    columnType: "select",
    sortable: false,
    minWidth: 120,
    draggable: false,
    className: "cursor-pointer",
  },
  {
    header: "Created Date",
    value: "",
    columnName: "createdOn",
    order: 13,
    columnType: "date",
    minWidth: 180,
    draggable: false,
    dateFormat: "DD MMM, YYYY",
    className: "cursor-pointer",
  },
  {
    header: "Created By",
    value: "",
    columnName: "userDisplayName",
    order: 13,
    columnType: "text",
    minWidth: 180,
    draggable: false,
    className: "cursor-pointer",
  },
];
export const WorkflowColumns = (callbacks = {}) => [
  {
    header: "Name",
    value: "",
    columnName: "name",
    columnType: "text",
    callbacks,
    draggable: false,
    minWidth: 180,
    order: 3,
    className: "cursor-pointer",
  },
  {
    header: "Created On",
    value: "",
    columnName: "createdOn",
    order: 13,
    columnType: "date",
    minWidth: 120,
    draggable: false,
    dateFormat: "DD MMM, YYYY",
    className: "cursor-pointer",
  },
  {
    header: "Created by",
    value: "",
    columnName: "userDisplayName",
    order: 13,
    columnType: "text",
    minWidth: 120,
    draggable: false,
    className: "cursor-pointer",
  },

  {
    header: "Type",
    value: "",
    columnName: "type",
    columnType: "select",
    minWidth: 80,
    // width: 100,
    draggable: false,
    enableFilter: true,
    className: "cursor-pointer",
  },
  {
    header: "Status",
    value: "",
    columnName: "workflowRunStatus",
    order: 13,
    columnType: "select",
    minWidth: 100,
    draggable: false,
  },
];

export const dashboardNewLeadsColumns = (callbacks = {}) => [
  {
    header: "Lead Name",
    value: "",
    columnName: "firstName",
    columnType: "text",
    callbacks,
    draggable: false,
    minWidth: 180,
    enableFilter: false,
  },
  {
    header: "Created By",
    // value: "",
    columnName: "LeadOwnerId",
    columnType: "select",
    minWidth: 180,
    enableFilter: false,
  },
  {
    header: "Date Created",
    value: "",
    columnName: "createdOn",
    columnType: "date",
    minWidth: 180,
    dateFormat: "DD MMM, YYYY",
    enableFilter: false,
  },
  {
    header: "Lead Status",
    columnName: "LeadStatusId",
    columnType: "select",
    enableFilter: false,
    // render: ({ data }) => (
    //   <Typography variant="body2">{data?.leadStatusId}</Typography>
    // ),
  },
];
export const UnassignedLeadsColumns = (callbacks = {}) => [
  {
    header: "ID",
    columnName: "id",
    draggable: false,
    columnType: "number",
    order: 1,
    render: ({ data }) => {
      return data?.id || "N/A";
    },
  },
  {
    header: "Lead Name",
    value: "",
    columnName: "leadName",
    columnType: "text",
    callbacks,
    draggable: false,
    minWidth: 180,
    render: ({ data }) => {
      return RenderLeadNameColumn(data, "leadName", callbacks?.leadId);
    },
  },
  {
    header: "Created By",
    // value: "",
    columnName: "createdBy",
    columnType: "text",
    minWidth: 180,
    enableFilter: true,
  },
  {
    header: "Created on",
    value: "",
    columnName: "createdOn",
    columnType: "date",
    minWidth: 180,
    dateFormat: "hh:mm A - DD MMM, YYYY",
  },
];

export const ReportColumns = (
  callbacks = {},
  checkFavs = () => {},
  copyClick = () => {}
) => [
  // {
  //   columnName: "Id",
  //   draggable: false,
  //   columnType: "number",
  //   order: 1,
  // },
  {
    header: "Name",
    value: "",
    columnName: "name",
    columnType: "text",
    callbacks,
    draggable: false,
    minWidth: 390,
    order: 3,
    defaultOperator: "insensitivecontains",
    render: ({ data }) => (
      <Typography
        className=" w-100 d-flex align-items-center justify-content-space-between"
        variant="body2"
      >
        <Box
          sx={{ width: "90%", textOverflow: "ellipsis", overflow: "hidden" }}
        >
          {data.name || "N/A"}
        </Box>
        <Typography variant="body2 d-flex align-items-center">
          {checkFavs(data?.id) ? (
            // {data?.isFavourite ? (
            <GetFontAwesomeIcon
              iconStyles={{
                marginLeft: "5px",
                color: "#FF8C22",
                cursor: "default",
              }}
              icon={faStar}
              size="sm"
            />
          ) : null}
          <span className="copy-button ">
            <CopyButton
              icon={faLink}
              showValue={false}
              value={copyClick(data?.id)}
            />
          </span>
        </Typography>
      </Typography>
    ),
  },
  // {
  //   title: "Type",
  //   value: "",
  //   columnName: "type",
  //   columnType: "select",
  //   // callbacks,
  //   draggable: false,
  //   minWidth: 180,
  //   order: 3,
  //   enableFilter: false,
  // },
  // TODO --- will be visible when done from BE
  // {
  //   header: "Access to",
  //   value: "",
  //   columnName: "accessTo",
  //   columnType: "text",
  //   width: 80,
  // },
  {
    header: "Report Type",
    value: "",
    columnName: "displayCategoryName",
    columnType: "text",
    minWidth: 80,
    // width: 100,
    draggable: false,
    enableFilter: true,
    defaultOperator: "insensitivecontains",
  },
  {
    header: "Created on",
    value: "",
    columnName: "createdOn",
    columnType: "date",
    minWidth: 150,
    dateFormat: "DD MMM, YYYY",
  },
  {
    header: "Created By",
    value: "",
    columnName: "displayName",
    columnType: "text",
    minWidth: 100,
    render: ({ data }) => (
      <Typography className="d-flex align-items-center" variant="body2">
        {data?.displayName || "N/A"}
      </Typography>
    ),
  },
  {
    header: "Last Viewed",
    value: "",
    columnName: "lastViewed",
    columnType: "date",
    minWidth: 150,
    dateFormat: "DD MMM, YYYY",
  },
  {
    header: "Last Updated",
    value: "",
    columnName: "modifiedOn",
    columnType: "date",
    minWidth: 150,
    dateFormat: "DD MMM, YYYY",
  },
];
export const RecentActivityCreatedLeads = (callbacks = {}) => [
  // {
  //   columnName: "Id",
  //   draggable: false,
  //   columnType: "number",
  //   order: 1,
  // },
  {
    header: "Lead Name",
    value: "",
    columnName: "leadName",
    columnType: "text",
    callbacks,
    enableFilter: false,
    minWidth: 160,
    render: ({ data }) => {
      return (data?.leadName || "").trim() || "N/A";
    },
  },
  // {
  //   title: "Type",
  //   value: "",
  //   columnName: "type",
  //   columnType: "select",
  //   // callbacks,
  //   draggable: false,
  //   minWidth: 180,
  //   order: 3,
  //   enableFilter: false,
  // },

  {
    header: "Created on",
    value: "",
    columnName: "leadStatusChangedOn",
    columnType: "date",
    minWidth: 150,
    enableFilter: false,
    dateFormat: "hh:mm A - DD MMM, YYYY",
  },
  {
    header: "Created By",
    value: "",
    columnName: "leadStatusChangedBy",
    columnType: "text",
    enableFilter: false,
    minWidth: 180,
  },
  {
    header: "Lead Status",
    value: "",
    columnName: "leadStatus",
    columnType: "text",
    minWidth: 160,
    enableFilter: false,
    render: ({ data }) => (
      <Chip
        label={data.leadStatus || "N/A"}
        sx={{
          background: "var(--palette-default_bg)",
          color: "var(--palette-default)",
        }}
      />
    ),
  },
  {
    header: "Lead Source",
    value: "",
    columnName: "leadSourceId",
    columnType: "select",
    minWidth: 160,
    enableFilter: false,
    renderRaw: true,
    // render: ({ data }) => <Chip label={data.leadStatus || "N/A"} />,
  },
];
export const AbandonedLeadsColumns = (callbacks = {}) => [
  // lastActivityDate
  // {
  //   columnName: "Id",
  //   draggable: false,
  //   columnType: "number",
  //   order: 1,
  // },
  {
    header: "First Name",
    value: "",
    columnName: "firstName",
    columnType: "text",
    callbacks,
    enableFilter: false,
    minWidth: 160,
    render: ({ data }) => {
      return (data?.firstName || "N/A").trim();
    },
  },
  {
    header: "Last Name",
    value: "",
    columnName: "lastName",
    columnType: "text",
    callbacks,
    enableFilter: false,
    minWidth: 160,
    render: ({ data }) => {
      return (data?.lastName || "N/A").trim();
    },
  },
  {
    key: 1,
    header: "Created on",
    value: "",
    columnName: "dateCreated",
    columnType: "date",
    minWidth: 150,
    enableFilter: false,
    dateFormat: "hh:mm A - DD MMM, YYYY",
  },
  {
    key: 2,
    header: "Days Inactive",
    value: "",
    columnName: "dateCreated",
    columnType: "text",
    callbacks,
    enableFilter: false,
    minWidth: 160,
    render: ({ data }) => {
      return (
        <Typography variant="body2">
          {moment(data?.dateCreated).fromNow().replace("ago", "")}
        </Typography>
      );
    },
  },
];
export const DashboardColumns = (callbacks = {}, compare = () => {}) => [
  // {
  //   header: "Id",
  //   columnName: "id",
  //   draggable: false,
  //   columnType: "number",
  //   width: 100,
  //   order: 1,
  // },
  {
    header: "Name",
    value: "",
    columnName: "name",
    columnType: "text",
    callbacks,
    minWidth: 180,
    render: ({ data }) => {
      return (
        <Box>
          {data?.name || "N/A"} {data.defaultDashboard ? <DefaultChip /> : null}
        </Box>
      );
    },
    // rowspan: ({ value, dataSourceArray, rowIndex, column }) => {
    //   console.log(value, dataSourceArray, rowIndex, column);
    //   return 1;
    // },
  },
  // {
  //   title: "Type",
  //   value: "",
  //   columnName: "type",
  //   columnType: "select",
  //   // callbacks,
  //   draggable: false,
  //   minWidth: 180,
  //   order: 3,
  //   enableFilter: false,
  // },

  {
    header: "Created on",
    value: "",
    columnName: "createdOn",
    columnType: "date",
    minWidth: 180,
    dateFormat: "DD MMM, YYYY",
  },
  {
    header: "Created By",
    value: "",
    columnName: "userDisplayName",
    columnType: "text",
    minWidth: 180,
  },
];

export const LeadsNotContactedColumns = (callbacks = {}) => [
  // {
  //   columnName: "Id",
  //   draggable: false,
  //   columnType: "number",
  //   order: 1,
  // },
  {
    header: "Lead Name",
    value: "",
    columnName: "leadName",
    columnType: "text",
    callbacks,
    draggable: false,
    minWidth: 180,
    render: ({ data }) => {
      return RenderLeadNameColumn(data, "leadName", callbacks?.leadId);
    },
  },

  {
    header: "Assigned to",
    value: "",
    columnName: "assignedTo",
    columnType: "select",
    minWidth: 180,
  },
  {
    header: "Date of lead generation",
    value: "",
    columnName: "dateOfLeadGeneration",
    columnType: "date",
    minWidth: 180,
    dateFormat: "hh:mm A - DD MMM, YYYY",
  },
];

export const LeadListInWorkflowsColumns = (callbacks = {}) => [
  {
    header: "Lead List",
    value: "",
    columnName: "leadList",
    columnType: "text",
    callbacks,
    enableFilter: false,
    minWidth: 180,
    render: ({ data }) => {
      return data.leadList || "N/A";
    },
  },
  {
    header: "Created On",
    value: "",
    columnName: "createdOn",
    columnType: "date",
    minWidth: 180,
    enableFilter: false,
    dateFormat: "hh:mm A - DD MMM, YYYY",
  },
  {
    header: "Created By",
    value: "",
    columnName: "createdBy",
    columnType: "text",
    minWidth: 180,
    enableFilter: false,
  },
  {
    header: "Automation Used In",
    value: "",
    columnName: "workflowUsedIn",
    columnType: "text",
    minWidth: 180,
    enableFilter: false,
  },
];

export const ActiveWorkflowsWithLeadsColumns = (callbacks = {}) => [
  {
    header: "Automation Name",
    value: "",
    columnName: "workflowName",
    columnType: "text",
    callbacks,
    minWidth: 180,
    enableFilter: false,
    render: ({ data }) => {
      return data.workflowName || "N/A";
    },
  },
  {
    header: "Created On",
    value: "",
    columnName: "workflowCreatedOn",
    columnType: "date",
    minWidth: 180,
    dateFormat: "hh:mm A - DD MMM, YYYY",
    enableFilter: false,
  },
  {
    header: "Number of Leads Enrolled",
    value: "",
    columnName: "numberOfLeadsEnrolled",
    columnType: "text",
    minWidth: 180,
    enableFilter: false,
  },
];
export const WorkflowsWithEmailTemplateColumns = (callbacks = {}) => [
  {
    header: "Automation Name",
    value: "",
    columnName: "workflowName",
    columnType: "text",
    callbacks,
    minWidth: 180,
    enableFilter: false,
    render: ({ data }) => {
      return data.workflowName || "N/A";
    },
  },
  {
    header: "Created On",
    value: "",
    columnName: "createdOn",
    columnType: "date",
    minWidth: 180,
    dateFormat: "hh:mm A - DD MMM, YYYY",
    enableFilter: false,
  },
  {
    header: "Created By",
    value: "",
    columnName: "createdBy",
    columnType: "text",
    minWidth: 180,
    enableFilter: false,
  },
  {
    header: "Template Name",
    value: "",
    columnName: "templateName",
    columnType: "text",
    minWidth: 180,
    enableFilter: false,
  },
];
export const WorkflowsWithSMSTemplateColumns = (callbacks = {}) => [
  {
    header: "Automation Name",
    value: "",
    columnName: "workflowName",
    columnType: "text",
    callbacks,
    minWidth: 180,
    enableFilter: false,
    render: ({ data }) => {
      return data.workflowName || "N/A";
    },
  },
  {
    header: "Created On",
    value: "",
    columnName: "createdOn",
    columnType: "date",
    minWidth: 180,
    dateFormat: "hh:mm A - DD MMM, YYYY",
    enableFilter: false,
  },
  {
    header: "Created By",
    value: "",
    columnName: "createdBy",
    columnType: "text",
    minWidth: 180,
    enableFilter: false,
  },
  {
    header: "Template Name",
    value: "",
    columnName: "templateName",
    columnType: "text",
    minWidth: 180,
    enableFilter: false,
  },
];

// main dashboard leads card columns
export const MainDashboardLeadsColumns = (callbacks = {}) => [
  {
    header: "Lead Name",
    value: "",
    columnName: "firstName",
    columnType: "text",
    callbacks,
    enableFilter: true,
    minWidth: 160,
    render: ({ data }) => {
      return RenderLeadNameColumn(
        data,
        "",
        callbacks?.leadId,
        !data?.firstName && !data?.lastName
          ? "N/A"
          : `${data.firstName || ""} ${data.lastName || ""}`
      );
    },
  },
  {
    header: "Lead Source",
    value: "",
    columnName: "leadSourceId",
    columnType: "select",
    enableFilter: true,
    minWidth: 130,
    // render: ({ data }) => <Chip label={data.leadStatus || "N/A"} />,
  },
  {
    header: "Date Created",
    value: "",
    columnName: "createdOn",
    columnType: "date",
    minWidth: 120,
    enableFilter: true,
    dateFormat: "DD MMM, YYYY",
  },

  {
    header: "Lead Status",
    value: "",
    columnName: "LeadStatusId",
    columnType: "select",
    enableFilter: true,
    minWidth: 130,
    // render: ({ data }) => <Chip label={data.leadStatus || "N/A"} />,
  },
];

export const TaskColumns = (callbacks = {}) => [
  {
    header: "Id",
    columnName: "Id",
    draggable: false,
    columnType: "number",
  },
  {
    header: "Title",
    value: "",
    columnName: "title",
    columnType: "text",
    draggable: false,
    minWidth: 180,
  },
  {
    header: "Associated Leads",
    value: "",
    columnName: "leadId",
    columnType: "text",
    callbacks,
    draggable: false,
    minWidth: 180,
    render: ({ data }) => {
      return (
        <Typography
          className={data?.leadId ? "text-underline cursor-pointer" : ""}
          onClick={
            callbacks?.leadId && data?.leadId
              ? (e) => callbacks?.leadId(e, data)
              : () => {}
          }
        >
          <a>
            {!data?.firstName && !data?.lastName
              ? data?.leadId || "N/A"
              : `${data.firstName || ""} ${data.lastName || ""}`}
          </a>
        </Typography>
      );
    },
  },
  // {
  //   title: "Type",
  //   value: "",
  //   columnName: "type",
  //   columnType: "select",
  //   // callbacks,
  //   draggable: false,
  //   minWidth: 180,
  //   order: 3,
  //   enableFilter: false,
  // },
  {
    header: "Type",
    value: "",
    columnName: "taskTypeId",
    columnType: "tags",
    minWidth: 100,
    draggable: false,
  },
  {
    header: "Priority",
    value: "",
    columnName: "taskPriorityId",
    columnType: "tags",
    bgfill: true,
    renderRaw: true,
    cellStyles: {
      height: "31px",
      width: "100%",
      px: 1,
      alignItems: "center",
      display: "flex",
      overflow: "hidden",
    },
    minWidth: 100,
    draggable: false,
  },
  {
    header: "Assigned to",
    value: "",
    columnName: "assignedTo",
    columnType: "select",
    minWidth: 180,
  },
  {
    header: "Status",
    value: "",
    columnName: "taskStatusId",
    columnType: "tags",
    minWidth: 100,
    draggable: false,
  },
  {
    header: "Due Date",
    value: "",
    columnName: "dueDate",
    columnType: "date",
    minWidth: 180,
    dateFormat: "DD MMM, YYYY",
    // isUTC: true,
  },
  {
    header: "Send Reminder",
    value: "",
    columnName: "reminder",
    columnType: "text",
    minWidth: 200,
    enableFilter: false,
    sortable: false,
    // dateFormat: "DD/MM/YYYY HH:mm A",
    render: ({ data }) => {
      // let obj = groupBy(
      //   sortArray([...data?.taskReminders] || [], "unit"),
      //   "unit"
      // );
      // let mapped = [];
      // Object.keys(obj).map((o) => {
      //   mapped.push(...sortArray(obj[o] || [], "value"));
      // });
      let mapped = [...data?.taskReminders].map((m) => makeRenderValue(m, ""));
      return (
        <Box className="d-flex" sx={{ gap: "5px" }}>
          {!mapped.length ? (
            "N/A"
          ) : mapped.length <= 2 ? (
            mapped.map((m, index) => <Chip key={index} label={m} />)
          ) : mapped.length > 2 ? (
            <>
              <Chip label={mapped[0]} />
              {RenderRemindersColumn(mapped)}
            </>
          ) : (
            "N/A"
          )}
        </Box>
      );
    },
  },
];

const RenderRemindersColumn = (data = []) => {
  return (
    <div className="">
      <UIPopper clickNode={<Chip label={`${data.length - 1}+`} />}>
        <Box
          sx={{
            p: 1,
            maxWidth: "150px",
            display: "flex",
            flexWrap: "wrap",
            gap: "10px",
          }}
        >
          {data.map((dt, index) => {
            return index > 0 ? <Chip key={index} label={dt} /> : null;
          })}
        </Box>
      </UIPopper>
    </div>
  );
};

export const LeadToAppointmentRatioColumns = (callbacks = {}) => [
  {
    header: "Lead Name",
    value: "",
    columnName: "leadName",
    columnType: "text",
    callbacks,
    draggable: false,
    minWidth: 180,
    render: ({ data }) => {
      return RenderLeadNameColumn(data, "leadName", callbacks?.leadId);
    },
  },
  {
    header: "Appointment Name",
    value: "",
    columnName: "appointmentName",
    columnType: "text",
    callbacks,
    minWidth: 180,
    render: ({ data }) => {
      return data.appointmentName || "N/A";
    },
  },
];

export const EmailReceivedColumns = (callbacks = {}) => [
  {
    header: "Lead Name",
    value: "",
    columnName: "leadName",
    columnType: "text",
    callbacks,
    draggable: false,
    minWidth: 180,
    render: ({ data }) => {
      return RenderLeadNameColumn(data, "leadName", callbacks?.leadId);
    },
  },
  {
    header: "Email Address",
    value: "",
    columnName: "email",
    columnType: "text",
    callbacks,
    minWidth: 180,
    render: ({ data }) => {
      return (data?.email || "").trim() || "N/A";
    },
  },
  {
    header: "Date",
    value: "",
    columnName: "createdOn",
    columnType: "date",
    minWidth: 120,
    dateFormat: "DD MMM, YYYY",
  },
];

export const EmailSentColumns = (callbacks = {}) => [
  {
    header: "Lead Name",
    value: "",
    columnName: "leadName",
    columnType: "text",
    callbacks,
    draggable: false,
    minWidth: 180,
    render: ({ data }) => {
      return RenderLeadNameColumn(data, "leadName", callbacks?.leadId);
    },
  },
  {
    header: "Email Address",
    value: "",
    columnName: "email",
    columnType: "text",
    callbacks,
    minWidth: 180,
    render: ({ data }) => {
      return (data?.email || "").trim() || "N/A";
    },
  },
  {
    header: "Sent Date",
    value: "",
    columnName: "createdOn",
    columnType: "date",
    minWidth: 120,
    dateFormat: "DD MMM, YYYY",
  },
  {
    header: "Status",
    value: "",
    columnName: "isOpened",
    columnType: "text",
    minWidth: 160,
    enableFilter: false,
    render: ({ data }) => (
      <Chip label={data?.isOpened ? "Opened" : "Not Opened"} />
    ),
  },
];

export const MQLLeadsByFirstOutReachColumns = (callbacks = {}) => [
  {
    header: "Qualified Name",
    value: "",
    columnName: "qualifiedLead",
    columnType: "text",
    callbacks,
    draggable: false,
    minWidth: 180,
    render: ({ data }) => {
      return RenderLeadNameColumn(data, "qualifiedLead", callbacks?.leadId);
    },
  },
  {
    header: "Over Time",
    value: "",
    columnName: "overTime",
    columnType: "date",
    minWidth: 120,
    dateFormat: "DD MMM, YYYY",
    render: ({ data }) =>
      getFormattedDate(data?.overTime, "DD MMM, YYYY", false),
  },
];

export const AppointmentScheduleColumns = (callbacks = {}) => [
  {
    header: "Appointment Title",
    value: "",
    columnName: "title",
    columnType: "text",
    callbacks,
    minWidth: 180,
    render: ({ data }) => {
      return (data?.title || "").trim() || "N/A";
    },
  },
  {
    header: "Lead Name",
    value: "",
    columnName: "leadName",
    columnType: "text",
    callbacks,
    draggable: false,
    minWidth: 180,
    render: ({ data }) => {
      return RenderLeadNameColumn(data, "leadName", callbacks?.leadId);
    },
  },
  {
    header: "Created By",
    value: "",
    columnName: "createdBy",
    columnType: "text",
    callbacks,
    minWidth: 180,
    render: ({ data }) => {
      return (data?.createdBy || "").trim() || "N/A";
    },
  },
  {
    header: "Date Created",
    value: "",
    columnName: "createdOn",
    columnType: "date",
    minWidth: 120,
    dateFormat: "DD MMM, YYYY",
    render: ({ data }) =>
      getFormattedDate(data?.createdOn, "DD MMM, YYYY", false),
  },
  {
    header: "Appointment Date",
    value: "",
    columnName: "startDate",
    columnType: "date",
    minWidth: 120,
    dateFormat: "DD MMM, YYYY",
    render: ({ data }) =>
      getFormattedDate(data?.startDate, "DD MMM, YYYY", true),
  },
  {
    header: "Appointment Time",
    value: "",
    columnName: "appointmentTime",
    columnType: "text",
    minWidth: 160,
    enableFilter: false,
    sortable: false,
    render: ({ data }) =>
      `${getFormattedDate(data?.startDate, "hh:mm A", true)} - ${getFormattedDate(data?.endDate, "hh:mm A", true)} ${getTimezoneAbbreviation()}`,
  },
];

export const SMSReceivedColumns = (callbacks = {}) => [
  {
    header: "Lead Name",
    value: "",
    columnName: "leadName",
    columnType: "text",
    callbacks,
    draggable: false,
    minWidth: 180,
    render: ({ data }) => {
      return RenderLeadNameColumn(data, "leadName", callbacks?.leadId);
    },
  },
  {
    header: "SMS Date",
    value: "",
    columnName: "createdOn",
    columnType: "date",
    minWidth: 120,
    dateFormat: "DD/MM/YYYY",
  },
];

export const LeadsGeneratedColumns = (callbacks = {}) => [
  {
    header: "Lead Name",
    value: "",
    columnName: "leadName",
    columnType: "text",
    callbacks,
    draggable: false,
    minWidth: 180,
    render: ({ data }) => {
      return RenderLeadNameColumn(data, "leadName", callbacks?.leadId);
    },
  },
  {
    header: "Phone",
    value: "",
    columnName: "phone",
    columnType: "text",
    callbacks,
    minWidth: 180,
    render: ({ data }) => {
      return (data?.phone || "").trim() || "N/A";
    },
  },
  {
    header: "Email",
    value: "",
    columnName: "email",
    columnType: "text",
    callbacks,
    minWidth: 180,
    render: ({ data }) => {
      return (data?.email || "").trim() || "N/A";
    },
  },
  {
    header: "Lead Status",
    value: "",
    columnName: "leadStatusId",
    options: "leadStatusId",
    columnType: "select",
    callbacks,
    draggable: false,
    minWidth: 180,
  },
];
export const SMSSentColumns = (callbacks = {}) => [
  {
    header: "Lead Name",
    value: "",
    columnName: "leadName",
    columnType: "text",
    callbacks,
    draggable: false,
    minWidth: 180,
    render: ({ data }) => {
      return RenderLeadNameColumn(data, "leadName", callbacks?.leadId);
    },
  },
  {
    header: "SMS Date",
    value: "",
    columnName: "createdOn",
    columnType: "date",
    minWidth: 120,
    dateFormat: "DD/MM/YYYY",
  },
  {
    header: "Status",
    value: "",
    columnName: "status",
    columnType: "text",
    minWidth: 160,
    enableFilter: false,
    render: ({ data }) => (
      <Chip
        label={camelCaseStringToNormalText(
          getObjectKeyByValue(Enums.SMSStatus, data?.status),
          "heading"
        )}
      />
    ),
  },
];

const RenderLeadNameColumn = (
  data = {},
  labelKey = "leadName",
  callback,
  tempValue = ""
) => {
  let value = (data?.[labelKey] || "").trim() || tempValue;
  return (
    <Typography
      className={
        value || data?.leadId || data?.id ? "text-underline cursor-pointer" : ""
      }
      onClick={callback ? (e) => callback(e, data) : () => {}}
    >
      <a>
        {value || "N/A"}
        {!value && (data?.leadId || data?.id)
          ? ` (${data?.leadId || data?.id})`
          : ""}
      </a>{" "}
    </Typography>
  );
};
